// Pure data logic for Time Reports' ACT Year over Year tab: the API returns two years of hours
// pre-summed per (month, person, project, task type, account, category, approved), and everything
// below slices that in the browser so filters and drill-downs need no round trip.

export const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const CATS = ['B', 'P', 'N', 'T'];
export const CAT_LABEL = { B: 'Billable', P: 'NB productive', N: 'Non-billable', T: 'Time off' };
export const CAT_COLOR = { B: 'var(--cat-billable)', P: 'var(--cat-nbp)', N: 'var(--cat-nb)', T: 'var(--cat-timeoff)' };
const CAT_OF = { Billable: 'B', NonBillableProductive: 'P', NonBillable: 'N', TimeOff: 'T' };
export const BILLED_LABEL = { Project: 'Project', Market: 'Market', CoreProduct: 'Core product', Administrative: 'Administrative' };

export const blank = () => ({ B: 0, P: 0, N: 0, T: 0 });
export const total = (o) => o.B + o.P + o.N + o.T;
export const pct = (a, b) => (b ? (a / b) * 100 : null);

// Flattens the API payload into short-keyed rows plus id lookups. Unknown categories count as
// non-billable rather than vanishing, so totals always match what was logged.
export function normalize(data) {
  const monthOf = new Map((data.months || []).map((m) => [m.periodMonth, m]));
  const users = new Map((data.users || []).map((u) => [u.id, u]));
  const teams = new Map((data.teams || []).map((t) => [t.id, t]));
  const projects = new Map((data.projects || []).map((p) => [p.id, p]));
  const taskTypes = new Map((data.taskTypes || []).map((t) => [t.id, t]));
  const rows = [];
  for (const r of data.rows || []) {
    const m = monthOf.get(r.periodMonth);
    if (!m) continue;
    rows.push({
      y: m.year, m: m.month - 1, p: r.userId, team: users.get(r.userId)?.teamId || '', pr: r.projectId,
      t: r.taskTypeId, a: r.account || '—', c: CAT_OF[r.category] || 'N', ok: r.approved, h: r.hours,
    });
  }
  const cap = new Map();
  for (const c of data.capacity || []) {
    const m = monthOf.get(c.periodMonth);
    if (m) cap.set(`${m.year}|${m.month - 1}|${c.userId}`, c.hours);
  }
  return { rows, cap, users, teams, projects, taskTypes, months: data.months || [] };
}

// Months 0-11 from January to the last CLOSED month of `year`; an in-progress month would compare a
// partial month against a full one. Falls back to the whole year when nothing has closed yet.
export function defaultRange(months, year) {
  const closed = months.filter((m) => m.year === year && m.closed).map((m) => m.month - 1);
  return { from: 0, to: closed.length ? Math.max(...closed) : 11 };
}

// Empty array = no filter, same convention as the other Time Reports tabs.
export function filterRows(norm, f) {
  const has = (list, v) => !list?.length || list.includes(v);
  return norm.rows.filter((r) => {
    if (r.m < f.from || r.m > f.to) return false;
    if (f.approvedOnly && !r.ok) return false;
    const pr = norm.projects.get(r.pr);
    const tt = norm.taskTypes.get(r.t);
    return has(f.teams, r.team) && has(f.people, r.p) && has(f.accounts, r.a) && has(f.projects, r.pr)
      && has(f.projectTypes, pr?.projectType) && has(f.cats, r.c) && has(f.taskTypes, r.t)
      && has(f.billedTo, tt?.activityBilledTo);
  });
}

export function sideOf(r, cy, py) {
  return r.y === cy ? 'cy' : r.y === py ? 'py' : null;
}

export function sumRows(rows, cy, py) {
  const out = { cy: blank(), py: blank() };
  for (const r of rows) {
    const s = sideOf(r, cy, py);
    if (s) out[s][r.c] += r.h;
  }
  return out;
}

export function monthSeries(rows, cy, py) {
  const out = { cy: MONTHS.map(blank), py: MONTHS.map(blank) };
  for (const r of rows) {
    const s = sideOf(r, cy, py);
    if (s) out[s][r.m][r.c] += r.h;
  }
  return out;
}

// The people capacity is counted for: the Team/Person filters only. Project/category filters narrow
// the hours, not who was available, so utilization under them reads "share of capacity spent on X".
export function scopePeople(norm, f) {
  return [...norm.users.values()]
    .filter((u) => (!f.teams?.length || f.teams.includes(u.teamId)) && (!f.people?.length || f.people.includes(u.id)))
    .map((u) => u.id);
}

export function capacity(norm, year, months, userIds) {
  let hours = 0;
  for (const m of months) for (const id of userIds) hours += norm.cap.get(`${year}|${m}|${id}`) || 0;
  return hours;
}

export const range = (from, to) => Array.from({ length: to - from + 1 }, (_, i) => from + i);

// Dimensions a row can be grouped by. `name` resolves an id for display.
export function dims(norm) {
  return {
    team: { label: 'Team', key: (r) => r.team, name: (k) => norm.teams.get(k)?.name || 'No team' },
    person: { label: 'Person', key: (r) => r.p, name: (k) => norm.users.get(k)?.name || 'Unknown' },
    account: { label: 'Account', key: (r) => r.a, name: (k) => k },
    project: { label: 'Project', key: (r) => r.pr, name: (k) => norm.projects.get(k)?.name || 'Unknown' },
    projectType: { label: 'Project type', key: (r) => norm.projects.get(r.pr)?.projectType || '—', name: (k) => k },
    cat: { label: 'Category', key: (r) => r.c, name: (k) => CAT_LABEL[k] },
    taskType: { label: 'Task type', key: (r) => r.t, name: (k) => norm.taskTypes.get(k)?.name || 'Unknown' },
    billedTo: { label: 'Billed to', key: (r) => norm.taskTypes.get(r.t)?.activityBilledTo || '—', name: (k) => BILLED_LABEL[k] || k },
    month: { label: 'Month', key: (r) => r.m, name: (k) => MONTHS[k] },
  };
}

// The natural next level when drilling into a row of `dim`.
export const NEXT_DIM = { team: 'person', person: 'project', project: 'taskType', account: 'project', projectType: 'project', cat: 'taskType', taskType: 'person', billedTo: 'taskType', month: 'team' };

export function groupBy(rows, keyFn) {
  const g = new Map();
  for (const r of rows) {
    const k = keyFn(r);
    if (!g.has(k)) g.set(k, []);
    g.get(k).push(r);
  }
  return g;
}

// Nested rows for the detail grid. Utilization only exists where every level up the path is Team
// or Person -- capacity belongs to people, so "utilization of a project" has no denominator.
export function buildTree(norm, rows, levels, f, cy, py, path = []) {
  if (path.length >= levels.length) return [];
  const D = dims(norm);
  const dim = levels[path.length];
  const months = range(f.from, f.to);
  return [...groupBy(rows, D[dim].key)].map(([k, rs]) => {
    const p = [...path, { dim, k }];
    const series = monthSeries(rs, cy, py);
    const sums = sumRows(rs, cy, py);
    let util = null;
    if (p.every((x) => x.dim === 'team' || x.dim === 'person')) {
      const ids = scopePeople(norm, f).filter((id) => p.every((x) => (x.dim === 'team' ? norm.users.get(id)?.teamId === x.k : id === x.k)));
      util = pct(sums.cy.B, capacity(norm, cy, months, ids));
    }
    return {
      id: p.map((x) => `${x.dim}:${x.k}`).join('|'), dim, k, name: D[dim].name(k), path: p,
      cyM: series.cy, pyM: series.py, cyT: sums.cy, pyT: sums.py, util,
      children: buildTree(norm, rs, levels, f, cy, py, p),
    };
  });
}

// Sorts copies; nulls last in either direction so blank utilization never tops a descending sort.
export function sortBy(items, sort, get) {
  return items.slice().sort((a, b) => {
    const x = get(a, sort.key);
    const y = get(b, sort.key);
    if (x == null && y == null) return 0;
    if (x == null) return 1;
    if (y == null) return -1;
    const d = typeof x === 'string' || typeof y === 'string' ? String(x).localeCompare(String(y)) : x - y;
    return sort.dir === 'asc' ? d : -d;
  });
}
